import { FC, FormEvent } from "react"
import { ErrorMessage, useFormikContext } from "formik"
import { Brands, LoanOfficer } from "../../../../types"
import { LO_ROLES_ORDERED, capitalize } from "../../../utils"
import Form from "../../form"
import WysiwygEditor from "../../form/WysiwygEditor"
import ImageUploader from "../../image-uploader"
import If from "../../if"

interface Props {
  title: string
  values: LoanOfficer
  brands: Brands
  showLoid?: boolean
  setFormField: (name: string, value: string | string[]) => void
}

const USER_ROLES = LO_ROLES_ORDERED.map(r => ({
  id: r,
  name: r
    .split(" ")
    .map(s => capitalize(s))
    .join(" ")
}))

const ProfileSection: FC<Props> = ({
  title,
  values,
  brands,
  showLoid,
  setFormField
}) => {
  const { errors, touched } = useFormikContext<any>()

  const isNMLSRequired =
    !values.roles ||
    values.roles.length !== 1 ||
    values.roles[0] !== "bank admin"

  const selectedBrand = brands.find(b => {
    if (b?.nid === values?.bank_nid) {
      return b
    }
    if (b?.id === values?.bank_nid) {
      return b
    }
    return undefined
  })

  const setRoles = (val: any[]) => {
    const roles: string[] = val.map(item => {
      if (typeof item === "string") {
        return item
      } else {
        return item.id
      }
    })
    setFormField("roles", roles)
  }

  const handleChangePicture = (url: string) => {
    setFormField("picture", url)
  }

  const imageURL = values?.picture === "delete" ? "" : values?.picture

  return (
    <div className="px-6">
      <h4 className="text-xl font-bold text-primary-alt-400 ">{title}</h4>

      <ImageUploader
        imageUrl={imageURL}
        onChangeImageUrl={handleChangePicture}
      />

      <If
        condition={!!showLoid && !!values?.loid}
        then={
          <div className="flex justify-center items-center w-24 text-sm gap-1 pt-1">
            <span className="font-medium">LOID:</span> {values?.loid}
          </div>
        }
      />

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-6">
          <Form.Select
            label="Brand"
            IDkey="id"
            selected={selectedBrand?.id ?? selectedBrand?.nid ?? undefined}
            data={brands}
            setSelected={val => setFormField("bank_nid", val)}
            required
            isError={!!(touched.bank_nid && errors.bank_nid)}
          />
          <ErrorMessage
            name={"bank_nid"}
            component="p"
            className="mt-2 text-sm text-red-600"
          />
        </div>
        <div className="col-span-6">
          <Form.Input
            label="NMLS"
            name="nmls"
            required={isNMLSRequired}
            inputProps={{
              onInput: (e: FormEvent<HTMLInputElement>) => {
                e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "")
              }
            }}
          />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-6">
          <Form.Select
            multiple
            label="Roles"
            name="roles"
            required
            selected={values?.roles ?? []}
            data={USER_ROLES}
            setSelected={setRoles}
            isError={!!(touched.roles && errors.roles)}
          />
          <ErrorMessage
            name="roles"
            component="p"
            className="mt-2 text-sm text-red-600"
          />
        </div>
        <div className="col-span-6">
          <Form.Input label="Title" name="position" />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-6">
          <Form.Input label="First name" name="first_name" required={true} />
        </div>
        <div className="col-span-6">
          <Form.Input label="Last name" name="last_name" required={true} />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-12">
          <Form.Input label="Email address" name="email" required={true} />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-12">
          <Form.Input
            label="Branch NMLS (Optional)"
            name="brand_nmls"
            inputProps={{
              onInput: (e: FormEvent<HTMLInputElement>) => {
                e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "")
              }
            }}
          />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-12">
          <Form.Input label="Pre Application link" name="app_link" type="url" />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-6">
          <Form.PhoneInput label="Mobile number" name="mobile_phone" />
        </div>
        <div className="col-span-6">
          <Form.PhoneInput label="Office number" name="office_phone" />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-6">
          <Form.PhoneInput label="Primary phone number" name="phone" />
        </div>
      </div>

      <div className="grid gap-y-6 gap-x-8 grid-cols-12 pt-8">
        <div className="col-span-12">
          <WysiwygEditor
            enableImageUpload
            autoHeight
            name="about_me"
            label="About me"
            initialValue={values?.about_me}
            setFieldValue={val => setFormField("about_me", val)}
          />
        </div>
      </div>
    </div>
  )
}

export default ProfileSection
